/**
 * PixelArt iFlow Generator
 * Converts uploaded images into pixelated SAP CPI iFlows.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { v, api } from "../utils/helpers";
import { FONT } from "../theme/tokens";

/* ── SVG Grid Icon ── */
const GridIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" style={{ verticalAlign: "middle" }}>
    {[0, 1, 2, 3].map(row =>
      [0, 1, 2, 3].map(col => (
        <rect
          key={`${row}-${col}`}
          x={col * 7}
          y={row * 7}
          width="6"
          height="6"
          rx="1"
          fill={(row + col) % 2 === 0 ? v("accent") : v("text-3")}
          opacity={(row + col) % 2 === 0 ? 1 : 0.5}
        />
      ))
    )}
  </svg>
);

/* ── Spinner ── */
const Spinner = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" style={{ animation: "pixelSpin 0.8s linear infinite", verticalAlign: "middle", marginRight: 8 }}>
    <circle cx="9" cy="9" r="7" stroke="rgba(255,255,255,.3)" strokeWidth="2.5" fill="none" />
    <path d="M9 2a7 7 0 0 1 7 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" fill="none" />
    <style>{`@keyframes pixelSpin { to { transform: rotate(360deg); transform-origin: center; } }`}</style>
  </svg>
);

/* ── Bilateral Filter + Gaussian Adaptive Threshold (tested winner) ── */
function processComplexImage(gray, w, h, maxPixels = 1000) {
  const sigmaSpace = 2.0, sigmaColor = 25.0, filterRadius = 3;
  const filtered = new Float64Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const centerVal = gray[y * w + x];
      let weightSum = 0, valueSum = 0;
      for (let dy = -filterRadius; dy <= filterRadius; dy++) {
        for (let dx = -filterRadius; dx <= filterRadius; dx++) {
          const ny = Math.max(0, Math.min(h - 1, y + dy));
          const nx = Math.max(0, Math.min(w - 1, x + dx));
          const neighborVal = gray[ny * w + nx];
          const spatialDist2 = dx * dx + dy * dy;
          const colorDist2 = (neighborVal - centerVal) * (neighborVal - centerVal);
          const weight = Math.exp(-spatialDist2 / (2 * sigmaSpace * sigmaSpace)) * Math.exp(-colorDist2 / (2 * sigmaColor * sigmaColor));
          weightSum += weight;
          valueSum += weight * neighborVal;
        }
      }
      filtered[y * w + x] = valueSum / weightSum;
    }
  }

  const blockSigma = 4.0, blockRadius = Math.ceil(blockSigma * 2.5), C = 6.0;
  const priority = new Float64Array(w * h);
  const grid = new Uint8Array(w * h);

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let weightedSum = 0, totalWeight = 0;
      for (let dy = -blockRadius; dy <= blockRadius; dy++) {
        for (let dx = -blockRadius; dx <= blockRadius; dx++) {
          const ny = Math.max(0, Math.min(h - 1, y + dy));
          const nx = Math.max(0, Math.min(w - 1, x + dx));
          const gw = Math.exp(-(dx * dx + dy * dy) / (2 * blockSigma * blockSigma));
          weightedSum += filtered[ny * w + nx] * gw;
          totalWeight += gw;
        }
      }
      const localMean = weightedSum / totalWeight;
      const pixelVal = filtered[y * w + x];
      priority[y * w + x] = localMean - pixelVal;
      grid[y * w + x] = pixelVal < localMean - C ? 1 : 0;
    }
  }

  let activeCount = 0;
  for (let i = 0; i < w * h; i++) if (grid[i]) activeCount++;

  let pixels = [];
  if (activeCount <= maxPixels) {
    for (let y = 0; y < h; y++)
      for (let x = 0; x < w; x++)
        if (grid[y * w + x]) pixels.push({ x, y, color: "#333333" });
  } else {
    const candidates = [];
    for (let y = 0; y < h; y++)
      for (let x = 0; x < w; x++)
        if (grid[y * w + x]) candidates.push({ x, y, p: priority[y * w + x] });
    candidates.sort((a, b) => b.p - a.p);
    pixels = candidates.slice(0, maxPixels).map(c => ({ x: c.x, y: c.y, color: "#333333" }));
  }
  return pixels;
}



/* ── Flow ID generator ── */
const generateId = (name) =>
  name.replace(/[^a-zA-Z0-9]/g, "").replace(/^(\d)/, "F$1") || "PixelArt";

/* ── Shared styles ── */
const cardStyle = {
  background: v("surface-1"),
  border: `1px solid ${v("border")}`,
  borderRadius: 12,
  padding: 24,
  marginBottom: 20,
};

const inputStyle = {
  background: v("surface-2"),
  border: `1px solid ${v("border")}`,
  borderRadius: 8,
  padding: "10px 14px",
  color: v("text-1"),
  fontFamily: FONT,
  fontSize: 14,
  lineHeight: "20px",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
  colorScheme: "dark",
};

const stepLabelStyle = {
  fontSize: 11,
  fontWeight: 600,
  color: v("accent"),
  textTransform: "uppercase",
  letterSpacing: 1,
  marginBottom: 4,
};

const sectionTitleStyle = {
  fontSize: 15,
  fontWeight: 600,
  color: v("text-1"),
  marginBottom: 16,
  marginTop: 0,
};

const btnPrimaryStyle = {
  background: v("accent"),
  color: "#fff",
  border: "none",
  borderRadius: 8,
  padding: "12px 24px",
  fontFamily: FONT,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

/* ── iFlow Preview (SVG) - mirrors backend layout exactly ── */
const IFlowPreview = ({ pixels, gridWidth, gridHeight }) => {
  if (!pixels || pixels.length === 0) return null;

  const EW = 60, EH = 60, ES = 32, HG = 10, VG = 10, MG = 80, PY = 80;

  const sorted = [...pixels].sort((a, b) => {
    if (a.y !== b.y) return a.y - b.y;
    return a.y % 2 === 0 ? a.x - b.x : b.x - a.x;
  });
  const N = sorted.length;

  const pp = (p) => ({
    x: MG + ES + HG + p.x * (EW + HG),
    y: PY + MG + p.y * (EH + VG),
  });

  const fp = pp(sorted[0]);
  const lp = sorted[N - 1];
  const lpP = pp(lp);
  const stXY = { x: MG, y: fp.y + (EH - ES) / 2 };
  const mx = Math.max(...sorted.map(p => p.x));
  const enRX = MG + ES + HG + mx * (EW + HG) + EW + HG;
  const enXY = { x: enRX, y: lpP.y + (EH - ES) / 2 };

  const maxRows = Math.max(...sorted.map(p => p.y)) + 1;
  const gridR = MG + ES + HG + gridWidth * (EW + HG);
  const laneW = Math.max(gridR, enXY.x + ES) + MG;
  const laneH = MG + maxRows * (EH + VG) + MG;

  const SX = -157, SY = 15, SW = 100, SH = 60;
  const vbX = SX - 10;
  const vbW = laneW - vbX + 10;
  const vbH = PY + laneH + 20;

  const tc = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) > 140 ? "#333" : "#fff";
  };

  const flows = [];
  flows.push([[stXY.x + ES, stXY.y + ES / 2], [fp.x, fp.y + EH / 2]]);
  for (let i = 1; i < N; i++) {
    const src = sorted[i - 1], tgt = sorted[i];
    const sP = pp(src), tP = pp(tgt);
    if (src.y === tgt.y) {
      if (src.y % 2 === 0) {
        flows.push([[sP.x + EW, sP.y + EH / 2], [tP.x, tP.y + EH / 2]]);
      } else {
        flows.push([[sP.x, sP.y + EH / 2], [tP.x + EW, tP.y + EH / 2]]);
      }
    } else {
      flows.push([[sP.x + EW / 2, sP.y + EH], [tP.x + EW / 2, tP.y]]);
    }
  }
  if (lp.y % 2 === 0) {
    flows.push([[lpP.x + EW, lpP.y + EH / 2], [enXY.x, enXY.y + ES / 2]]);
  } else {
    const belowY = lpP.y + EH + VG / 2;
    flows.push([
      [lpP.x + EW / 2, lpP.y + EH],
      [lpP.x + EW / 2, belowY],
      [enXY.x + ES / 2, belowY],
      [enXY.x + ES / 2, enXY.y],
    ]);
  }

  return (
    <svg
      viewBox={`${vbX} 0 ${vbW} ${vbH}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: "100%", maxHeight: 400, background: "#fff", borderRadius: 8, border: `1px solid ${v("border")}` }}
    >
      <defs>
        <marker id="pa-arrow" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
          <polygon points="0 0, 8 3, 0 6" fill="#888" />
        </marker>
      </defs>
      <rect x={SX} y={SY} width={SW} height={SH} rx="6" fill="#e3f2fd" stroke="#90caf9" strokeWidth="1.5"/>
      <text x={SX + SW / 2} y={SY + SH / 2 + 4} textAnchor="middle" fontSize="11" fill="#1565c0" style={{ fontFamily: FONT }}>Sender</text>
      <rect x={0} y={PY} width={laneW} height={laneH} rx="6" fill="#fafafa" stroke="#ccc" strokeWidth="1.5"/>
      <line x1={SX + SW} y1={SY + SH / 2} x2={stXY.x + ES / 2} y2={stXY.y} stroke="#999" strokeWidth="1" strokeDasharray="6,4" markerEnd="url(#pa-arrow)"/>
      <circle cx={stXY.x + ES / 2} cy={stXY.y + ES / 2} r={ES / 2} fill="#e8f5e9" stroke="#66bb6a" strokeWidth="2"/>
      <text x={stXY.x + ES / 2} y={stXY.y + ES + 14} textAnchor="middle" fontSize="9" fill="#888">Start</text>
      {sorted.map((p, i) => {
        const pos = pp(p);
        return (
          <g key={i}>
            <rect x={pos.x} y={pos.y} width={EW} height={EH} rx="4" fill={p.color} stroke="#555" strokeWidth="1"/>
            {N <= 25 && (
              <text x={pos.x + EW / 2} y={pos.y + EH / 2 + 4} textAnchor="middle" fontSize="9" fill={tc(p.color)} style={{ fontFamily: FONT }}>
                Px {p.x},{p.y}
              </text>
            )}
          </g>
        );
      })}
      <circle cx={enXY.x + ES / 2} cy={enXY.y + ES / 2} r={ES / 2} fill="#ffebee" stroke="#ef5350" strokeWidth="3"/>
      <text x={enXY.x + ES / 2} y={enXY.y + ES + 14} textAnchor="middle" fontSize="9" fill="#888">End</text>
      {flows.map((wps, i) => {
        if (wps.length === 2) {
          return <line key={`f${i}`} x1={wps[0][0]} y1={wps[0][1]} x2={wps[1][0]} y2={wps[1][1]} stroke="#888" strokeWidth="1.2" markerEnd="url(#pa-arrow)"/>;
        }
        const d = wps.map(([x, y], j) => `${j === 0 ? "M" : "L"}${x} ${y}`).join(" ");
        return <path key={`f${i}`} d={d} fill="none" stroke="#888" strokeWidth="1.2" markerEnd="url(#pa-arrow)"/>;
      })}
    </svg>
  );
};

export default function PixelArt({ isGuest = false }) {
  const { t } = useTranslation("pixelart");

  /* ── State ── */
  const [instances, setInstances] = useState([]);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [flowName, setFlowName] = useState("");
  const [flowId, setFlowId] = useState("");
  const [imageUrl, setImageUrl] = useState(null);
  const [gridSize, setGridSize] = useState(30);
  const [gridDims, setGridDims] = useState({ w: 0, h: 0 });
  const [pixelData, setPixelData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // { type: "success"|"error", message }
  const [detectionMode, setDetectionMode] = useState(null); // "logo" | "complex"
  const [outputMode, setOutputMode] = useState(isGuest ? "download" : "upload"); // "upload" | "download"

  const originalCanvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const flowIdTouched = useRef(false);

  /* ── Load instances on mount (skip for guests) ── */
  useEffect(() => {
    if (isGuest) return;
    (async () => {
      try {
        const data = await api("/api/pixel-art/instances");
        setInstances(data || []);
      } catch {
        setInstances([]);
      }
    })();
  }, [isGuest]);

  /* ── Load packages when instance changes ── */
  useEffect(() => {
    if (!selectedInstance) {
      setPackages([]);
      setSelectedPackage(null);
      return;
    }
    (async () => {
      setPackagesLoading(true);
      setSelectedPackage(null);
      try {
        const data = await api(`/api/pixel-art/packages/${selectedInstance}`);
        setPackages(data || []);
      } catch {
        setPackages([]);
      }
      setPackagesLoading(false);
    })();
  }, [selectedInstance]);

  /* ── Auto-generate flowId from flowName (unless manually edited) ── */
  useEffect(() => {
    if (!flowIdTouched.current) setFlowId(generateId(flowName));
  }, [flowName]);

  /* ── Process image → pixel data + draw canvases ── */
  const processImage = useCallback(
    (dataUrl, maxDim) => {
      const img = new Image();
      img.onload = () => {
        // 1) Draw original preview
        const origCanvas = originalCanvasRef.current;
        if (origCanvas) {
          const maxOrig = 200;
          const scale = Math.min(maxOrig / img.width, maxOrig / img.height, 1);
          origCanvas.width = Math.round(img.width * scale);
          origCanvas.height = Math.round(img.height * scale);
          const origCtx = origCanvas.getContext("2d");
          origCtx.imageSmoothingEnabled = true;
          origCtx.drawImage(img, 0, 0, origCanvas.width, origCanvas.height);
        }

        // 2) Calculate grid dims preserving aspect ratio
        const aspect = img.width / img.height;
        let gw, gh;
        if (aspect >= 1) {
          gw = maxDim;
          gh = Math.max(1, Math.round(maxDim / aspect));
        } else {
          gh = maxDim;
          gw = Math.max(1, Math.round(maxDim * aspect));
        }

        // 3) Downsample to gw x gh
        const offscreen = document.createElement("canvas");
        offscreen.width = gw;
        offscreen.height = gh;
        const offCtx = offscreen.getContext("2d");
        offCtx.imageSmoothingEnabled = true;
        offCtx.fillStyle = "#FFFFFF";
        offCtx.fillRect(0, 0, gw, gh);
        offCtx.drawImage(img, 0, 0, gw, gh);
        const imgData = offCtx.getImageData(0, 0, gw, gh);

        // Build grayscale + count non-white pixels
        const gray = new Float32Array(gw * gh);
        let nonWhiteCount = 0;
        for (let y = 0; y < gh; y++) {
          for (let x = 0; x < gw; x++) {
            const i = (y * gw + x) * 4;
            const brightness = 0.299 * imgData.data[i] + 0.587 * imgData.data[i + 1] + 0.114 * imgData.data[i + 2];
            gray[y * gw + x] = brightness;
            if (brightness < 240) nonWhiteCount++;
          }
        }

        const totalPixels = gw * gh;
        const nonWhiteRatio = nonWhiteCount / totalPixels;

        let pixels;
        if (nonWhiteRatio > 0.5) {
          setDetectionMode("complex");
          pixels = processComplexImage(gray, gw, gh, 1000);
        } else {
          // Simple image (logo, icon) → direct pixel extraction
          setDetectionMode("logo");
          pixels = [];
          for (let y = 0; y < gh; y++) {
            for (let x = 0; x < gw; x++) {
              if (gray[y * gw + x] < 240) {
                const i = (y * gw + x) * 4;
                const hex = "#" + [imgData.data[i], imgData.data[i + 1], imgData.data[i + 2]].map((c) => c.toString(16).padStart(2, "0")).join("");
                pixels.push({ x, y, color: hex });
              }
            }
          }
        }
        setPixelData(pixels);
        setGridDims({ w: gw, h: gh });
      };
      img.src = dataUrl;
    },
    []
  );

  useEffect(() => {
    if (imageUrl) processImage(imageUrl, gridSize);
  }, [gridSize, imageUrl, processImage]);

  /* ── File upload handler ── */
  const handleFileUpload = (e) => {
    if (loading) return;
    const file = e.target.files?.[0];
    if (!file) return;
    setStatus(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImageUrl(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  /* ── Generate action ── */
  const canGenerate = outputMode === "upload"
    ? selectedInstance && selectedPackage && flowName.trim() && flowId.trim() && pixelData.length > 0 && pixelData.length <= 1000 && !loading
    : flowName.trim() && flowId.trim() && pixelData.length > 0 && pixelData.length <= 1000 && !loading;

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setLoading(true);
    setStatus(null);

    if (outputMode === "download") {
      try {
        const resp = await fetch("/api/pixel-art/download-zip", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            flowId,
            flowName: flowName.trim(),
            pixels: pixelData,
            gridWidth: gridDims.w,
            gridHeight: gridDims.h,
          }),
        });
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.error || `HTTP ${resp.status}`);
        }
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${flowId}.zip`;
        a.click();
        URL.revokeObjectURL(url);
        setStatus({ type: "success", message: t("meldung.zipGeladen", { datei: `${flowId}.zip` }) });
      } catch (err) {
        setStatus({ type: "error", message: err.message || t("meldung.downloadFehler") });
      }
      setLoading(false);
      return;
    }

    const ac = new AbortController();
    const tm = setTimeout(() => ac.abort(), 180000);
    try {
      const res = await api("/api/pixel-art/generate", {
        method: "POST",
        body: JSON.stringify({
          instanceId: selectedInstance,
          packageId: selectedPackage,
          flowId,
          flowName: flowName.trim(),
          pixels: pixelData,
          gridWidth: gridDims.w,
          gridHeight: gridDims.h,
        }),
        signal: ac.signal,
      });
      setStatus({ type: "success", message: res.message || t("meldung.iflowErzeugt") });
    } catch (err) {
      setStatus({ type: "error", message: err.message || t("meldung.generierungFehler") });
    }
    clearTimeout(tm);
    setLoading(false);
  };

  /* ── Render ── */
  return (
    <div
      style={{
        maxWidth: 800,
        margin: "0 auto",
        padding: 32,
        fontFamily: FONT,
        color: v("text-1"),
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
        <GridIcon />
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: v("text-1"), fontFamily: FONT }}>
          {t("kopf.titel")}
        </h1>
      </div>

      {/* Step 1 - Ausgabemodus & Instanz */}
      <div style={cardStyle}>
        <div style={stepLabelStyle}>{t("schritt.eins")}</div>
        <h3 style={sectionTitleStyle}>{t("ausgabe.titel")}</h3>

        {/* Mode Toggle (only for logged-in users) */}
        {!isGuest && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {[
            { value: "upload", label: t("ausgabe.hochladen") },
            { value: "download", label: t("ausgabe.herunterladen") },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setOutputMode(opt.value)}
              style={{
                flex: 1,
                padding: "10px 16px",
                borderRadius: 8,
                border: `1.5px solid ${outputMode === opt.value ? v("accent") : v("border")}`,
                background: outputMode === opt.value ? v("accent") : v("surface-2"),
                color: outputMode === opt.value ? "#fff" : v("text-2"),
                fontFamily: FONT,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
        )}

        {/* Instance & Package (only for upload mode) */}
        {outputMode === "upload" && (
        <div style={{ display: "flex", gap: 16 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 13, color: v("text-2"), marginBottom: 6, display: "block", fontFamily: FONT }}>
              {t("ausgabe.instanz")}
            </label>
            <select
              style={inputStyle}
              value={selectedInstance || ""}
              onChange={(e) => setSelectedInstance(e.target.value || null)}
              onFocus={(e) => (e.target.style.borderColor = v("accent"))}
              onBlur={(e) => (e.target.style.borderColor = v("border"))}
            >
              <option value="">{t("ausgabe.instanzWaehlen")}</option>
              {instances.map((inst) => (
                <option key={inst.id} value={inst.id}>
                  {inst.name} ({inst.tenantUrl})
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 13, color: v("text-2"), marginBottom: 6, display: "block", fontFamily: FONT }}>
              {t("ausgabe.package")}
            </label>
            <select
              style={{ ...inputStyle, opacity: !selectedInstance ? 0.5 : 1 }}
              value={selectedPackage || ""}
              onChange={(e) => setSelectedPackage(e.target.value || null)}
              disabled={!selectedInstance}
              onFocus={(e) => (e.target.style.borderColor = v("accent"))}
              onBlur={(e) => (e.target.style.borderColor = v("border"))}
            >
              <option value="">
                {packagesLoading ? t("ausgabe.packageLaedt") : selectedInstance ? t("ausgabe.packageWaehlen") : t("ausgabe.zuerstInstanz")}
              </option>
              {packages.map((pkg) => (
                <option key={pkg.Id} value={pkg.Id}>
                  {pkg.Name} (v{pkg.Version})
                </option>
              ))}
            </select>
          </div>
        </div>
        )}

        {outputMode === "download" && (
          <div style={{ padding: "12px 16px", borderRadius: 8, background: v("surface-2"), fontSize: 13, color: v("text-2"), fontFamily: FONT, lineHeight: 1.5 }}>
            {t("ausgabe.downloadHinweis")}
          </div>
        )}
      </div>

      {/* Step 2 - iFlow Konfiguration */}
      <div style={cardStyle}>
        <div style={stepLabelStyle}>{t("schritt.zwei")}</div>
        <h3 style={sectionTitleStyle}>{t("konfiguration.titel")}</h3>
        <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 13, color: v("text-2"), marginBottom: 6, display: "block", fontFamily: FONT }}>
              {t("konfiguration.flowName")}
            </label>
            <input
              type="text"
              style={inputStyle}
              placeholder={t("konfiguration.flowNamePlatzhalter")}
              value={flowName}
              onChange={(e) => setFlowName(e.target.value)}
              onFocus={(e) => (e.target.style.borderColor = v("accent"))}
              onBlur={(e) => (e.target.style.borderColor = v("border"))}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 13, color: v("text-2"), marginBottom: 6, display: "block", fontFamily: FONT }}>
              {t("konfiguration.bezeichner")}
            </label>
            <input
              type="text"
              style={inputStyle}
              placeholder="PixelArt"
              value={flowId}
              onChange={(e) => { flowIdTouched.current = true; setFlowId(e.target.value); }}
              onFocus={(e) => (e.target.style.borderColor = v("accent"))}
              onBlur={(e) => (e.target.style.borderColor = v("border"))}
            />
            <div style={{ fontSize: 11, color: v("text-3"), marginTop: 4, fontFamily: FONT }}>
              {t("konfiguration.bezeichnerHinweis")}
            </div>
          </div>
        </div>
      </div>

      {/* Step 3 - Bild hochladen & Pixeln */}
      <div style={cardStyle}>
        <div style={stepLabelStyle}>{t("schritt.drei")}</div>
        <h3 style={sectionTitleStyle}>{t("bild.titel")}</h3>

        {/* Upload + Slider Row */}
        <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 20 }}>
          {/* File Upload */}
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 18px",
              background: v("surface-2"),
              border: `1px solid ${v("border")}`,
              borderRadius: 8,
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
              fontFamily: FONT,
              color: v("text-1"),
              transition: "border-color 0.15s",
              whiteSpace: "nowrap",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = v("accent"))}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = v("border"))}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 1v10M4 5l4-4 4 4M2 12v1a2 2 0 002 2h8a2 2 0 002-2v-1"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {t("bild.auswaehlen")}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleFileUpload}
            />
          </label>

          {/* Grid Size Slider */}
          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 12 }}>
            <label style={{ fontSize: 13, color: v("text-2"), fontFamily: FONT, whiteSpace: "nowrap" }}>
              {t("bild.gridGroesse")}
            </label>
            <input
              type="range"
              min={5}
              max={50}
              value={gridSize}
              onChange={(e) => setGridSize(Number(e.target.value))}
              disabled={loading}
              style={{ flex: 1, accentColor: v("accent"), margin: "0 6px" }}
            />
            <span
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: v("accent"),
                fontFamily: FONT,
                minWidth: 40,
                textAlign: "center",
              }}
            >
              {gridDims.w && gridDims.h ? `${gridDims.w} x ${gridDims.h}` : `${gridSize} x ${gridSize}`}
            </span>
          </div>
        </div>

        {/* Canvas Previews */}
        {imageUrl ? (
          <>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 20, marginBottom: 16 }}>
              <div style={{ textAlign: "center", flexShrink: 0 }}>
                <div style={{ fontSize: 12, color: v("text-3"), marginBottom: 8, fontFamily: FONT, fontWeight: 600 }}>
                  {t("bild.original")}
                </div>
                <canvas
                  ref={originalCanvasRef}
                  style={{
                    borderRadius: 8,
                    border: `1px solid ${v("border")}`,
                    maxWidth: 140,
                    maxHeight: 140,
                    background: v("surface-2"),
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: v("text-3"), marginBottom: 8, fontFamily: FONT, fontWeight: 600, textAlign: "center" }}>
                  {t("bild.vorschau", { modifier: pixelData.length, pixel: gridDims.w * gridDims.h })}
                  {detectionMode && (
                    <span style={{ marginLeft: 8, fontSize: 11, padding: "2px 8px", borderRadius: 4, background: detectionMode === "complex" ? "rgba(99,102,241,0.15)" : "rgba(52,211,153,0.15)", color: detectionMode === "complex" ? "#818cf8" : "#34d399" }}>
                      {detectionMode === "complex" ? t("bild.modusSchwelle") : t("bild.modusLogo")}
                    </span>
                  )}
                </div>
                <IFlowPreview pixels={pixelData} gridWidth={gridDims.w} gridHeight={gridDims.h} />
                {pixelData.length > 1000 && (
                  <div style={{ marginTop: 8, padding: "8px 12px", borderRadius: 6, fontSize: 12, fontFamily: FONT, background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.3)", color: "#F87171" }}>
                    {t("bild.zuVieleModifier", { anzahl: pixelData.length })}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div
            style={{
              border: `2px dashed ${v("border")}`,
              borderRadius: 12,
              padding: "48px 24px",
              textAlign: "center",
              color: v("text-3"),
              fontSize: 14,
              fontFamily: FONT,
            }}
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              style={{ marginBottom: 12, opacity: 0.4 }}
            >
              <rect x="2" y="2" width="36" height="36" rx="6" stroke="currentColor" strokeWidth="2" />
              <circle cx="14" cy="14" r="3" fill="currentColor" opacity="0.5" />
              <path d="M6 30l8-10 6 7 4-5 10 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div>{t("bild.leer")}</div>
          </div>
        )}
      </div>

      {/* Generate Button */}
      <button
        style={{
          ...btnPrimaryStyle,
          opacity: canGenerate ? 1 : 0.5,
          cursor: canGenerate ? "pointer" : "not-allowed",
        }}
        disabled={!canGenerate}
        onClick={handleGenerate}
      >
        {loading ? (
          <>
            <Spinner />
            {outputMode === "download" ? t("aktion.zipLaeuft") : t("aktion.iflowLaeuft")}
          </>
        ) : (
          outputMode === "download" ? t("aktion.zip") : t("aktion.iflow")
        )}
      </button>

      {/* Status Message */}
      {status && (
        <div
          style={{
            marginTop: 16,
            padding: "14px 18px",
            borderRadius: 8,
            fontSize: 14,
            fontFamily: FONT,
            fontWeight: 500,
            background: status.type === "success" ? "rgba(52,211,153,0.1)" : "rgba(248,113,113,0.1)",
            border: `1px solid ${status.type === "success" ? "rgba(52,211,153,0.3)" : "rgba(248,113,113,0.3)"}`,
            color: status.type === "success" ? "#34D399" : "#F87171",
          }}
        >
          {status.type === "success" ? (
            <span style={{ marginRight: 8 }}>&#10003;</span>
          ) : (
            <span style={{ marginRight: 8 }}>&#10007;</span>
          )}
          {status.message}
        </div>
      )}
    </div>
  );
}
